// RITA-1543 — Karte „Vertragserstellung“ im Bereich „Vertrag“ beim Berater, direkt unter „Vertrag &
// Organisation“ (Skizze Bild 1 und 1b, freigegeben von Julia am 19.10.2026): links Titel und „Passend
// für …“, rechts in derselben Höhe der Knopf „Vertrag erstellen“; bei schmalem Fenster darunter.
//
// Nur für Super-Admins eingehängt (ContractPanel); verbindlich prüft der Server (getContractCreation).
// Grundlage sind die GESPEICHERTEN Angaben: Bei ungespeicherten Änderungen ist der Knopf gesperrt (F3),
// ein neuer Berater bekommt den Hinweis aus F4. Nach dem Speichern fragt die Karte neu ab.
// Die Optik entspricht PanelCard (pages/AgentEditPage), ohne von dort zu importieren.
//
// RITA-1547 (Bildentwurf 3b, Bilder B und D): Gibt es einen Entwurf, zeigt die Karte darunter die
// Entwurfszeile, und „Vertrag erstellen“ fragt zuerst „weiterbearbeiten oder neu beginnen?“. „Neu beginnen“
// öffnet das Fenster wie bisher; der Entwurf bleibt, bis ein neuer gespeichert wird.
//
// RITA-1549 (Skizze 3c, Bilder B und D; F2, F6, F8, F10): Die Karte hält den Druck-Hook (useVertragDrucken) für
// das Fenster, „Ansehen“ und „Erneut drucken“; so überlebt der Druck das Schließen des Fensters, und der Hinweis
// nach einem misslungenen Dateiabruf hat hier seinen Platz. Unten steht das Aktionsprotokoll (GET_DRUCKPROTOKOLL,
// nur Antworten zum aktuellen Berater); „Vertrag erstellen“ wartet darauf wie auf den Entwurfsstand, ein
// Ladefehler bietet „Erneut versuchen“. Gibt es schon einen Druck MIT Vertrag, fragt die Karte vor dem Fenster
// „Schon ein Vertrag gedruckt“ (Bild B) – bei vorhandenem Entwurf erst nach „Neu beginnen“ der Rückfrage B,
// „Weiterbearbeiten“ fragt nicht (F2). Nach jedem Druck schließt sie das Fenster und lädt Protokoll und Entwurf neu.
//
// RITA-1552 (Skizze Rücklauf vom 21.10.2026, Bilder A, C und E; F1–F3, F5, F8 und Entscheidungen E3, E6 von Julia
// freigegeben): Rechts oben stehen drei Knöpfe, von links nach rechts „Unterschriebenen Vertrag hochladen“ · „Rücksendung
// drucken“ (beide mit Rahmen) · „Vertrag erstellen“ (grün wie bisher); bei schmalem Fenster rutschen sie unter den Titel.
// „Passend für …“ nennt auch das Anschreiben Rücksendung (Abfrage mit allen drei Arten); hat nur die Rücksendung eine
// Warnung, verweist der Hinweis darunter auf das Fenster „Rücksendung drucken“ (Abschlussreview MR 2, F3). „Hochladen“
// ist grau, solange kein Vertrag im PRM gedruckt ist, mit dem Hinweis aus Bild A e beim Darüberfahren oder per Tastatur
// (die Hülle ist fokussierbar), und öffnet das Fenster aus Bild C.
// Jeder Knopf wartet nur auf das, was er braucht (Kritik P3, Abschlussreview MR 2, Technik T1): „Vertrag erstellen“
// auf Vorlagenwahl und Vertragsentwurf, „Rücksendung drucken“ auf Vorlagenwahl und den Entwurf der Rücksendung (eigener
// Entwurf mit eigener Abfrage, eigener Zeile direkt unter der Zeile des Vertragsentwurfs und eigenem Ladefehler),
// „Hochladen“ auf keines davon; alle drei auf das Aktionsprotokoll und gespeicherte Angaben, und während eines Drucks
// sind alle gesperrt. „Rücksendung drucken“ fragt der Reihe nach: Entwurf der Rücksendung (3b-Muster) → „Schon eine
// Rücksendung gedruckt“ (nur zu einem Vertrag aus dem PRM, E3) → Fenster. Je Vorgang merkt sich die Karte die Revision
// aus „Neu beginnen“ (`ersetzt`). Das Fenster „Rücksendung drucken“ ist das Fenster „Vertrag erstellen“ als Vorgang
// Rücksendung (Bild D): Es bekommt den neuesten im PRM gedruckten Vertrag („Gehört zu“, im Fall B keinen), ob es zu ihm
// schon einen Scan gibt, und die Revision der Rücksendung aus „Neu beginnen“. Nach jedem Druck und jedem Hochladen lädt
// die Karte Protokoll und beide Entwürfe neu; die Liste „Dokumente“ lädt die jeweilige Mutation mit der Berater-UUID aus
// der Antwort neu.
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Tooltip, Typography } from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useFormikContext } from 'formik';
import DataContext from '../../../context/DataProvider/DataContext';
import { GET_VERTRAGSAUSWAHL, VertragsauswahlAntwort, VertragsauswahlVariablen } from '../api/vertragserstellung';
import { ENTWURFSDOKUMENTE, VertragsentwurfAntwort, VertragsentwurfVariablen } from '../api/vertragsentwurf';
import { DruckprotokollAntwort, DruckprotokollVariablen, GET_DRUCKPROTOKOLL } from '../api/vertragsdruck';
import { PagePathname } from '../../../routes/paths';
import useVertragDrucken from '../hooks/useVertragDrucken';
import { letzteRuecksendungFuer, letzterVertragsdruck, scanVorhandenFuer } from '../services/vertragsbloecke';
import {
  ABGELEGT_NICHT_GEOEFFNET,
  HOCHLADEN_GESPERRT_TOOLTIP,
  KNOPF_HOCHLADEN,
  KNOPF_RUECKSENDUNG,
  PROTOKOLL_LADEFEHLER,
} from '../services/vertragsdruckTexte';
import { KARTE_ENTWURF_LADEFEHLER, KARTE_RUECKSENDUNGSENTWURF_LADEFEHLER } from '../services/vertragsentwurfTexte';
import { StammdatenBezug, stammdatenKontext, vollerName } from '../services/vertragPlatzhalterwerte';
import { ALLE_ARTEN, Vorgang, vorgangSuche } from '../services/vorgang';
import {
  BITTE_SPEICHERN,
  einzelheitenHinweis,
  ERNEUT_VERSUCHEN,
  KARTEN_TITEL,
  kartenteil,
  KNOPF_TEXT,
  LADEFEHLER_KARTE,
  NEUER_BERATER,
  passendFuer,
  WIRD_GESUCHT,
} from '../services/vertragserstellungTexte';
import Aktionsprotokoll from './Aktionsprotokoll';
import { SchonGedrucktDialog, SchonRuecksendungDialog } from './DruckDialoge';
import VertragErstellenDialog from './VertragErstellenDialog';
import { EntwurfVorhandenDialog } from './EntwurfDialoge';
import EntwurfZeile from './EntwurfZeile';
import ScanHochladenDialog from './ScanHochladenDialog';

type Beraterformular = StammdatenBezug & { agent_id: number };

/** RITA-1552: Welches Fenster über der Karte offen ist (höchstens eines). */
type Fenster = 'vertrag' | 'ruecksendung' | 'hochladen';

/** Such-Parameter der Bearbeiten-Seite je Vorgang: beim Vertrag keiner, bei der Rücksendung `?vorgang=ruecksendung`. */
const entwurfSuche = (vorgang: Vorgang) => {
  const suche = vorgangSuche(vorgang);
  return suche ? `?${suche}` : '';
};

/**
 * Der Entwurf eines Vorgangs, wie die Karte ihn zeigt. `network-only`: Ein Stand aus dem Cache kann veraltet sein (das
 * Speichern auf der Bearbeiten-Seite aktualisiert ihn nicht); die Karte zeigt und entscheidet nur nach der Antwort des
 * Servers. Ein erfolgreich verworfener Stand ist weg, auch wenn das anschließende Neuladen scheitert (KI-Review !779).
 * `bekannt`: Erst dann kann der Knopf des Vorgangs richtig fragen (Bild B); auch während eines Neuladens gilt der Stand
 * als unbekannt, denn vorhandene Daten können veraltet sein.
 */
function useKartenentwurf(vorgang: Vorgang, agentId: number, neu: boolean) {
  const abfrage = useQuery<VertragsentwurfAntwort, VertragsentwurfVariablen>(ENTWURFSDOKUMENTE[vorgang].abfrage, {
    variables: { agent_id: agentId },
    skip: neu,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [verworfen, setVerworfen] = useState<string | null>(null);
  const daten = abfrage.data?.getContractCreationDraft;
  return {
    entwurf: daten && daten.agent_id === agentId && daten.revision !== verworfen ? daten : null,
    bekannt: !abfrage.loading && !abfrage.error && abfrage.data !== undefined,
    ladefehler: !neu && !abfrage.loading && !!abfrage.error,
    neuLaden: () => abfrage.refetch(),
    setVerworfen,
  };
}

/** Ladefehler unter der Kopfzeile mit „Erneut versuchen“ (beide Entwürfe, Aktionsprotokoll). */
function Ladefehler({ testId, text, onErneut }: { testId?: string; text: string; onErneut: () => void }) {
  return (
    <Alert
      severity="error"
      sx={{ mt: 1.5 }}
      data-testid={testId}
      action={
        <Button color="inherit" size="small" sx={{ textTransform: 'none' }} onClick={onErneut}>
          {ERNEUT_VERSUCHEN}
        </Button>
      }
    >
      {text}
    </Alert>
  );
}

/** RITA-1552: „Unterschriebenen Vertrag hochladen“ (Bild A) – mit Rahmen wie „Rücksendung drucken“. */
function HochladenKnopf({ gesperrt, onClick }: { gesperrt: boolean; onClick: () => void }) {
  return (
    <Button
      variant="outlined"
      startIcon={<FileUploadOutlinedIcon />}
      disabled={gesperrt}
      onClick={onClick}
      data-testid="scan-hochladen-knopf"
      sx={{ textTransform: 'none' }}
    >
      {KNOPF_HOCHLADEN}
    </Button>
  );
}

function KarteEinesBeraters() {
  const { initialValues, values, dirty } = useFormikContext<Beraterformular>();
  const { brachOffices, country, status, rank, level, contractType, agents } = useContext(DataContext);
  const agentId = Number(initialValues.agent_id) || 0;
  const neu = agentId <= 0;
  const [fenster, setFenster] = useState<Fenster | null>(null);
  // RITA-1552: Rückfrage B „Es gibt schon einen Entwurf“ – für den Entwurf welchen Vorgangs.
  const [vorhandenFrage, setVorhandenFrage] = useState<Vorgang | null>(null);
  // RITA-1549: Rückfrage „Schon ein Vertrag gedruckt“ (Bild B) vor dem Fenster.
  const [schonGedrucktFrage, setSchonGedrucktFrage] = useState(false);
  // RITA-1601 (Fund 128): Kennung des Drucks mit Vertrag, dessen Ersetzen „Trotzdem neuen erstellen“ bestätigt hat.
  // „Bearbeiten“ im Fenster gibt sie der Bearbeiten-Seite mit (`ersetztVertrag`), damit die nicht noch einmal fragt.
  const [bestaetigterVertragsdruck, setBestaetigterVertragsdruck] = useState<number | null>(null);
  // RITA-1552: Rückfrage „Schon eine Rücksendung gedruckt“ (Skizze Rücklauf, Bild D a) vor dem Fenster.
  const [schonRuecksendungFrage, setSchonRuecksendungFrage] = useState(false);
  // Revision des Entwurfs, den die Rückfrage B gezeigt hat; nur ihn darf ein neuer Anfang ersetzen. RITA-1552: je
  // Vorgang, denn Vertrag und Rücksendung haben je einen eigenen Entwurf.
  const [ersetzt, setErsetzt] = useState<Record<Vorgang, string | null>>({ contract: null, return: null });
  const navigate = useNavigate();

  const { data, loading, error, refetch } = useQuery<VertragsauswahlAntwort, VertragsauswahlVariablen>(
    GET_VERTRAGSAUSWAHL,
    {
      // RITA-1552: alle drei Arten – „Passend für …“ nennt auch das Anschreiben Rücksendung (Bild A).
      variables: { agent_id: agentId, kinds: ALLE_ARTEN },
      skip: neu,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
  );
  // Nur die Antwort zum aktuellen Berater zeigen (kein Stand eines vorher geöffneten Beraters).
  const auswahl = data?.getContractCreation?.agent_id === agentId ? data.getContractCreation : null;
  const vertragsentwurf = useKartenentwurf('contract', agentId, neu);
  const ruecksendungsentwurf = useKartenentwurf('return', agentId, neu);
  const { entwurf } = vertragsentwurf;
  const { entwurf: ruecksendungEntwurf } = ruecksendungsentwurf;

  // RITA-1549: Aktionsprotokoll, aus demselben Grund `network-only` wie der Entwurf (der Druck von der
  // Bearbeiten-Seite und das Löschen unter „Dokumente“ schreiben den Cache nicht). Nur Einträge zum aktuellen
  // Berater zählen.
  const protokollAbfrage = useQuery<DruckprotokollAntwort, DruckprotokollVariablen>(GET_DRUCKPROTOKOLL, {
    variables: { agent_id: agentId },
    skip: neu,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const protokollDaten = protokollAbfrage.data?.getContractCreationPrintLog;
  const eintraege = useMemo(
    () => (protokollDaten ?? []).filter((eintrag) => eintrag.agent_id === agentId),
    [protokollDaten, agentId],
  );
  const protokollLadefehler = !neu && !protokollAbfrage.loading && !!protokollAbfrage.error;
  const protokollNeuLaden = () => protokollAbfrage.refetch();
  // Entscheidung 4: Nur ein früherer Druck MIT Vertrag löst die Rückfrage „schon gedruckt“ aus.
  const letzterDruck = useMemo(() => letzterVertragsdruck(eintraege), [eintraege]);
  // RITA-1552 (E3): Eine frühere Rücksendung zählt nur zum neuesten Vertrag aus dem PRM; ohne ihn (Fall B) keine.
  const letzteRuecksendung = useMemo(
    () => (letzterDruck ? letzteRuecksendungFuer(eintraege, letzterDruck.id) : null),
    [eintraege, letzterDruck],
  );

  /** Nach Druck oder Hochladen: Protokoll und beide Entwürfe neu laden (der Stand kann sich geändert haben). */
  const nachAenderungNeuLaden = () => {
    protokollNeuLaden().catch(() => undefined);
    vertragsentwurf.neuLaden().catch(() => undefined);
    ruecksendungsentwurf.neuLaden().catch(() => undefined);
  };

  const druck = useVertragDrucken({
    agentId,
    // Nach jedem Druck (Fenster, Erneut drucken) – auch wenn nur der Dateiabruf scheiterte: Fenster zu, Protokoll
    // und Entwürfe neu laden (der Druck aus dem Fenster erledigt keinen Entwurf, F10; der Stand kann sich trotzdem
    // inzwischen geändert haben).
    onGedruckt: () => {
      setFenster(null);
      nachAenderungNeuLaden();
    },
    onProtokollNeuLaden: () => {
      protokollNeuLaden().catch(() => undefined);
    },
  });

  // Nach dem Speichern setzt die Beraterseite neue initialValues: Vorlagenwahl neu abfragen.
  const gespeicherterStand = useRef(initialValues);
  useEffect(() => {
    if (gespeicherterStand.current === initialValues) return;
    gespeicherterStand.current = initialValues;
    if (!neu) refetch().catch(() => undefined);
  }, [initialValues, neu, refetch]);

  const bearbeitenSeite = (suche = '') =>
    navigate(`${PagePathname.beraterVertragBearbeiten.replace(':number', String(agentId))}${suche}`);

  const wertekontext = useMemo(
    () =>
      stammdatenKontext(values, {
        firmen: brachOffices,
        laender: country,
        status,
        raenge: rank,
        level,
        vertragsarten: contractType,
        berater: agents,
      }),
    [values, brachOffices, country, status, rank, level, contractType, agents],
  );

  const ladefehler = !neu && !loading && !!error;
  const bereit = !neu && !ladefehler && !!auswahl;
  // RITA-1547: Erst wenn feststeht, ob es einen Entwurf gibt, kann „Vertrag erstellen“ richtig fragen (Bild B).
  // RITA-1549: Dasselbe gilt für das Aktionsprotokoll, sonst fehlte beim ersten Klick die Rückfrage „schon gedruckt“.
  const protokollBekannt = !protokollAbfrage.loading && !protokollAbfrage.error && protokollAbfrage.data !== undefined;
  // Auch während eines laufenden Drucks/Abrufs (Ansehen, Erneut drucken) gesperrt: onGedruckt schließt sonst ein
  // inzwischen geöffnetes Fenster (KI-Review !785). RITA-1552: Jeder Entwurf sperrt nur den Knopf seines Vorgangs.
  const grundsperre = dirty || !protokollBekannt || druck.laeuft;
  const gesperrt = grundsperre || !bereit || !vertragsentwurf.bekannt;
  const ruecksendungGesperrt = grundsperre || !bereit || !ruecksendungsentwurf.bekannt;
  // RITA-1552 (F2, Bild A e): grau ohne im PRM gedruckten Vertrag; der Hinweis erst, wenn das Protokoll das belegt.
  // Vorlagenwahl und Entwürfe spielen für das Hochladen keine Rolle (Abschlussreview MR 2, Technik T1).
  const hochladenGesperrt = grundsperre || !letzterDruck;
  const hochladenHinweis = protokollBekannt && !letzterDruck;
  const teile = auswahl ? auswahl.documents.map(kartenteil) : [];
  const einzelheiten = auswahl ? einzelheitenHinweis(auswahl.documents) : null;

  /** Fenster öffnen; gibt es schon einen Vertragsdruck, zuerst die Rückfrage aus Bild B (Entscheidung 5). */
  const fensterOeffnen = () => {
    if (letzterDruck) setSchonGedrucktFrage(true);
    else setFenster('vertrag');
  };

  /** RITA-1552 (Bild D a): gibt es zum Vertrag schon eine Rücksendung, zuerst „Schon eine Rücksendung gedruckt“. */
  const ruecksendungOeffnen = () => {
    if (letzteRuecksendung) setSchonRuecksendungFrage(true);
    else setFenster('ruecksendung');
  };

  /** Klick auf den Knopf eines Vorgangs: erst der Entwurf (Rückfrage B), sonst ohne bestätigte Revision weiter. */
  const vorgangStarten = (vorgang: Vorgang) => {
    // RITA-1601: Jeder Klick beginnt ohne bestätigtes Ersetzen; die Rückfrage „Schon ein Vertrag gedruckt“ setzt es neu.
    if (vorgang === 'contract') setBestaetigterVertragsdruck(null);
    const vorhanden = vorgang === 'return' ? ruecksendungEntwurf : entwurf;
    if (vorhanden) {
      setVorhandenFrage(vorgang);
      return;
    }
    setErsetzt((bisher) => ({ ...bisher, [vorgang]: null }));
    if (vorgang === 'return') ruecksendungOeffnen();
    else fensterOeffnen();
  };
  const vorhandenEntwurf = vorhandenFrage === 'return' ? ruecksendungEntwurf : entwurf;

  let unterzeile: React.ReactNode = null;
  if (neu) {
    unterzeile = <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{NEUER_BERATER}</Typography>;
  } else if (ladefehler) {
    unterzeile = (
      <Alert
        severity="error"
        sx={{ mt: 1 }}
        action={
          <Button
            color="inherit"
            size="small"
            sx={{ textTransform: 'none' }}
            onClick={() => refetch().catch(() => undefined)}
          >
            {ERNEUT_VERSUCHEN}
          </Button>
        }
      >
        {LADEFEHLER_KARTE}
      </Alert>
    );
  } else if (!auswahl) {
    unterzeile = <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{WIRD_GESUCHT}</Typography>;
  } else {
    unterzeile = (
      <>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }} data-testid="vertragserstellung-passend">
          {passendFuer(auswahl)}
          <br />
          {teile.map((teil, index) => (
            // eslint-disable-next-line react/no-array-index-key -- feste Reihenfolge Anschreiben, Vertrag, Rücksendung
            <React.Fragment key={index}>
              {index > 0 && ' · '}
              {teil.warnung ? (
                <Box component="span" sx={{ color: 'warning.main', fontWeight: 600 }}>
                  {teil.text}
                </Box>
              ) : (
                teil.text
              )}
            </React.Fragment>
          ))}
        </Typography>
        {einzelheiten && (
          <Typography
            sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }}
            data-testid="vertragserstellung-einzelheiten"
          >
            {einzelheiten}
          </Typography>
        )}
        {dirty && (
          <Alert severity="warning" sx={{ mt: 1 }} data-testid="vertragserstellung-speichern">
            {BITTE_SPEICHERN}
          </Alert>
        )}
      </>
    );
  }

  return (
    <Box
      data-testid="vertragserstellung-karte"
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: { xs: 2.5, sm: 3.5 },
        mb: 2,
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}
      >
        <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 800, mb: 0.5 }}>{KARTEN_TITEL}</Typography>
          {unterzeile}
        </Box>
        <Box
          sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}
          data-testid="vertragserstellung-knoepfe"
        >
          {/* Ein gesperrter Knopf löst keine Mausereignisse aus und ist nicht fokussierbar: Der Hinweis hängt an einer
              Hülle (MUI Tooltip), die per Tab erreichbar ist und den Hinweis als Namen trägt (Abschlussreview MR 2, T2). */}
          {hochladenHinweis ? (
            <Tooltip title={HOCHLADEN_GESPERRT_TOOLTIP}>
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Hinweis zum grauen Knopf per Tastatur */}
              <span data-testid="scan-hochladen-huelle" tabIndex={0}>
                <HochladenKnopf gesperrt={hochladenGesperrt} onClick={() => setFenster('hochladen')} />
              </span>
            </Tooltip>
          ) : (
            <HochladenKnopf gesperrt={hochladenGesperrt} onClick={() => setFenster('hochladen')} />
          )}
          <Button
            variant="outlined"
            startIcon={<MailOutlineIcon />}
            disabled={ruecksendungGesperrt}
            onClick={() => vorgangStarten('return')}
            data-testid="ruecksendung-drucken-knopf"
            sx={{ textTransform: 'none' }}
          >
            {KNOPF_RUECKSENDUNG}
          </Button>
          <Button
            variant="contained"
            startIcon={<NoteAddOutlinedIcon />}
            disabled={gesperrt}
            onClick={() => vorgangStarten('contract')}
            data-testid="vertrag-erstellen-knopf"
            sx={{ textTransform: 'none' }}
          >
            {KNOPF_TEXT}
          </Button>
        </Box>
      </Box>
      {entwurf && (
        <EntwurfZeile
          agentId={agentId}
          entwurf={entwurf}
          gesperrt={dirty || druck.laeuft}
          onWeiterbearbeiten={() => bearbeitenSeite(entwurfSuche('contract'))}
          onVerworfen={() => vertragsentwurf.setVerworfen(entwurf.revision)}
          onNeuLaden={vertragsentwurf.neuLaden}
        />
      )}
      {/* RITA-1552 (Bild E, F8): die Zeile des Rücksendungs-Entwurfs direkt unter der des Vertragsentwurfs. */}
      {ruecksendungEntwurf && (
        <EntwurfZeile
          agentId={agentId}
          entwurf={ruecksendungEntwurf}
          vorgang="return"
          gesperrt={dirty || druck.laeuft}
          onWeiterbearbeiten={() => bearbeitenSeite(entwurfSuche('return'))}
          onVerworfen={() => ruecksendungsentwurf.setVerworfen(ruecksendungEntwurf.revision)}
          onNeuLaden={ruecksendungsentwurf.neuLaden}
        />
      )}
      {vertragsentwurf.ladefehler && (
        <Ladefehler
          testId="vertragsentwurf-ladefehler"
          text={KARTE_ENTWURF_LADEFEHLER}
          onErneut={() => {
            vertragsentwurf.neuLaden().catch(() => undefined);
          }}
        />
      )}
      {ruecksendungsentwurf.ladefehler && (
        <Ladefehler
          testId="ruecksendungsentwurf-ladefehler"
          text={KARTE_RUECKSENDUNGSENTWURF_LADEFEHLER}
          onErneut={() => {
            ruecksendungsentwurf.neuLaden().catch(() => undefined);
          }}
        />
      )}
      <Aktionsprotokoll
        eintraege={eintraege}
        laeuft={druck.laeuft}
        onAnsehen={(filename) => {
          druck.ansehen(filename).catch(() => undefined);
        }}
        onErneutDrucken={(entryId) => {
          druck.erneutDrucken(entryId).catch(() => undefined);
        }}
      />
      {protokollLadefehler && (
        <Ladefehler
          testId="vertragsdruck-ladefehler"
          text={PROTOKOLL_LADEFEHLER}
          onErneut={() => {
            protokollNeuLaden().catch(() => undefined);
          }}
        />
      )}
      {druck.fehler && fenster === null && (
        // Hinweis des Hooks außerhalb des Fensters: abgelegt, aber nicht geöffnet; Fehler bei Ansehen/Erneut drucken.
        // Im offenen Fenster zeigt das Fenster seinen Fehler selbst; beim Schließen wird er verworfen.
        <Alert
          severity={druck.fehler === ABGELEGT_NICHT_GEOEFFNET ? 'warning' : 'error'}
          sx={{ mt: 1.5 }}
          data-testid="vertragsdruck-fehler"
          onClose={druck.fehlerSchliessen}
        >
          {druck.fehler}
        </Alert>
      )}
      {vorhandenFrage && vorhandenEntwurf && (
        <EntwurfVorhandenDialog
          entwurf={vorhandenEntwurf}
          vorgang={vorhandenFrage}
          onAbbrechen={() => setVorhandenFrage(null)}
          onNeuBeginnen={() => {
            setVorhandenFrage(null);
            setErsetzt((bisher) => ({ ...bisher, [vorhandenFrage]: vorhandenEntwurf.revision }));
            if (vorhandenFrage === 'return') ruecksendungOeffnen();
            else fensterOeffnen();
          }}
          onWeiterbearbeiten={() => {
            setVorhandenFrage(null);
            bearbeitenSeite(entwurfSuche(vorhandenFrage));
          }}
        />
      )}
      {schonGedrucktFrage && letzterDruck && (
        <SchonGedrucktDialog
          datumIso={letzterDruck.created_at}
          onAbbrechen={() => setSchonGedrucktFrage(false)}
          onTrotzdem={() => {
            setSchonGedrucktFrage(false);
            // RITA-1601 (Fund 128): genau dieser Vertrag darf ersetzt werden – auch von der Bearbeiten-Seite aus.
            setBestaetigterVertragsdruck(letzterDruck.id);
            setFenster('vertrag');
          }}
        />
      )}
      {schonRuecksendungFrage && letzterDruck && letzteRuecksendung && (
        <SchonRuecksendungDialog
          vertragIso={letzterDruck.created_at}
          ruecksendungIso={letzteRuecksendung.created_at}
          onAbbrechen={() => setSchonRuecksendungFrage(false)}
          onTrotzdem={() => {
            setSchonRuecksendungFrage(false);
            setFenster('ruecksendung');
          }}
        />
      )}
      {fenster === 'vertrag' && (
        <VertragErstellenDialog
          onClose={() => {
            setFenster(null);
            druck.fehlerSchliessen();
          }}
          agentId={agentId}
          beraterName={vollerName(values.name, values.surname)}
          beraterwerte={values}
          wertekontext={wertekontext}
          ersetztRevision={ersetzt.contract}
          ersetztVertragsdruck={bestaetigterVertragsdruck}
          drucken={druck.drucken}
          laeuft={druck.laeuft}
        />
      )}
      {/* RITA-1552 (Bild D): dasselbe Fenster als Vorgang Rücksendung, nur eingehängt, solange es offen ist. */}
      {fenster === 'ruecksendung' && (
        <VertragErstellenDialog
          vorgang="return"
          gehoertZu={letzterDruck}
          scanVorhanden={scanVorhandenFuer(eintraege, letzterDruck?.id)}
          onClose={() => {
            setFenster(null);
            druck.fehlerSchliessen();
          }}
          agentId={agentId}
          beraterName={vollerName(values.name, values.surname)}
          beraterwerte={values}
          wertekontext={wertekontext}
          ersetztRevision={ersetzt.return}
          drucken={druck.drucken}
          laeuft={druck.laeuft}
        />
      )}
      {fenster === 'hochladen' && (
        <ScanHochladenDialog
          agentId={agentId}
          beraterName={vollerName(values.name, values.surname)}
          eintraege={eintraege}
          onClose={() => setFenster(null)}
          onHochgeladen={() => {
            setFenster(null);
            nachAenderungNeuLaden();
          }}
          onProtokollNeuLaden={() => {
            protokollNeuLaden().catch(() => undefined);
          }}
        />
      )}
    </Box>
  );
}

/**
 * Rückfrage, Fenster und bestätigte Revision gehören zu genau einem Berater. Die Beraterseite bleibt eingehängt,
 * wenn nur die Beraternummer wechselt (etwa per Sprung im Verlauf des Browsers); ohne eigenen Schlüssel nähme die
 * Karte sie zum nächsten Berater mit (Nachprüfung zum Kollegenreview !779).
 */
function VertragserstellungKarte() {
  const { initialValues } = useFormikContext<Beraterformular>();
  const agentId = Number(initialValues.agent_id) || 0;
  return <KarteEinesBeraters key={agentId} />;
}

export default VertragserstellungKarte;
